import { Event } from '@/types/revenue-manager';
import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { mockEventsData } from '@/data/mock-data';
import { transformEventFromAPI, transformEventForAPI, RawApiEvent } from '@/utils/event-transform';

export interface EventsResponse {
  success: boolean;
  data: Event[];
  total: number;
  propertyId: string;
}

export interface UpcomingEventsCountResponse {
  success: boolean;
  count: number;
}

// ============ API Functions ============

export async function fetchEvents(propertyId: string): Promise<EventsResponse> {
  if (USE_MOCK) {
    await delay();
    // Get events that include this property
    const allEvents: Event[] = [];
    Object.values(mockEventsData).forEach(events => {
      events.forEach(event => {
        if (event.propertyIds.includes(propertyId) && !allEvents.find(e => e.id === event.id)) {
          allEvents.push(event);
        }
      });
    });
    
    return {
      success: true,
      data: allEvents,
      total: allEvents.length,
      propertyId,
    };
  }
  
  // Use generic API endpoint with filters parameter
  const filters = JSON.stringify([{
    resource_name: "RevenueEvent",
    columns: ["*", "property_id"],
    column_filters: [{
      property_id: propertyId
    }]
  }]);
  
  const response = await apiClient.get<any>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`
  );
  
  // console.log('Full Events API Response:', JSON.stringify(response.data, null, 2));
  // Extract data from nested structure
  const rawEvents = (response.data?.data?.[0] as any)?.RevenueEvent_data || [];
  // console.log('Raw events data length:', rawEvents.length);
  
  // if (rawEvents.length > 0) {
  //   console.log('First event structure:', JSON.stringify(rawEvents[0], null, 2));
  // }
  
  // Transform API format to internal format
  const transformedEvents = rawEvents.map((rawEvent: unknown, index: number) => {
    // console.log(`Transforming event ${index}:`, rawEvent);
    try {
      const transformed = transformEventFromAPI(rawEvent as RawApiEvent);
      // console.log(`Transformed event ${index}:`, transformed);
      return transformed;
    } catch (error) {
      console.error(`Error transforming event ${index}:`, error);
      throw error;
    }
  });
  // console.log('Transformed events:', transformedEvents);
  
  return {
    success: response.data.success,
    data: transformedEvents,
    total: transformedEvents.length,
    propertyId,
  };
}

export async function fetchAllEvents(): Promise<ApiResponse<Event[]> & { total: number }> {
  if (USE_MOCK) {
    await delay();
    const allEvents: Event[] = [];
    Object.values(mockEventsData).forEach(events => {
      events.forEach(event => {
        if (!allEvents.find(e => e.id === event.id)) {
          allEvents.push(event);
        }
      });
    });
    
    return {
      success: true,
      data: allEvents,
      total: allEvents.length,
    };
  }
  
  // Use generic API endpoint with filters parameter
  const filters = JSON.stringify([{
    resource_name: "RevenueEvent",
    columns: ["*"],
    column_filters: []
  }]);
  
  const response = await apiClient.get<any>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`
  );
  
  // console.log('Full All Events API Response:', JSON.stringify(response.data, null, 2));
  // Extract data from nested structure
  const rawEvents = (response.data?.data?.[0] as any)?.RevenueEvent_data || [];
  
  // Transform API format to internal format
  const transformedEvents = rawEvents.map((rawEvent: unknown) => 
    transformEventFromAPI(rawEvent as RawApiEvent)
  );
  
  return {
    success: response.data.success,
    data: transformedEvents,
    total: transformedEvents.length,
  };
}

export async function fetchUpcomingEvents(): Promise<UpcomingEventsCountResponse> {
  if (USE_MOCK) {
    await delay();
    const today = new Date();
    const allEvents: Event[] = [];
    Object.values(mockEventsData).forEach(events => {
      events.forEach(event => {
        if (!allEvents.find(e => e.id === event.id)) {
          allEvents.push(event);
        }
      });
    });
    
    const upcomingEvents = allEvents.filter(e => new Date(e.startDate) >= today);
    
    return {
      success: true,
      count: upcomingEvents.length,
    };
  }
  
  // Format current date as YYYY-MM-DD
  const today = new Date();
  const currentDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Build filters for generic endpoint
  const filters = JSON.stringify([{
    resource_name: "RevenueEvent",
    columns: ["*", "start_date"],
    column_filters: [{ start_date: currentDate }]
  }]);
  
  const filterOp = JSON.stringify([{
    resource_name: "RevenueEvent",
    start_date: "gte"
  }]);
  
  const response = await apiClient.get<ApiResponse<any>>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}&filter_op=${encodeURIComponent(filterOp)}`
  );
  
  const eventsData = response.data?.data?.[0]?.RevenueEvent_data || [];
  
  return {
    success: response.data.success,
    count: eventsData.length,
  };
}

export async function fetchEventById(eventId: string): Promise<ApiResponse<Event | null>> {
  if (USE_MOCK) {
    await delay(200);
    let foundEvent: Event | null = null;
    Object.values(mockEventsData).forEach(events => {
      const event = events.find(e => e.id === eventId);
      if (event) foundEvent = event;
    });
    
    return {
      success: !!foundEvent,
      data: foundEvent,
    };
  }
  
  const response = await apiClient.get<ApiResponse<unknown>>(
    API_ENDPOINTS.events.get(eventId)
  );
  
  const transformedEvent = response.data.data 
    ? transformEventFromAPI(response.data.data as RawApiEvent) 
    : null;
  
  return {
    success: response.data.success,
    data: transformedEvent,
  };
}

export async function createEvent(event: Omit<Event, 'id'>): Promise<ApiResponse<Event>> {
  if (USE_MOCK) {
    await delay();
    const newEvent: Event = {
      ...event,
      id: `event-${Date.now()}`,
    };
    
    // Store under first property
    const firstPropertyId = event.propertyIds[0] || '1';
    if (!mockEventsData[firstPropertyId]) {
      mockEventsData[firstPropertyId] = [];
    }
    mockEventsData[firstPropertyId].push(newEvent);
    
    return {
      success: true,
      data: newEvent,
    };
  }
  
  const apiPayload = transformEventForAPI(event);
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.events.create,
    apiPayload
  );
  
  const transformedEvent = response.data.data 
    ? transformEventFromAPI(response.data.data as RawApiEvent) 
    : null;
  
  return {
    success: response.data.success,
    data: transformedEvent as Event,
  };
}

export async function updateEvent(
  eventId: string,
  updates: Partial<Event>
): Promise<ApiResponse<Event | null>> {
  if (USE_MOCK) {
    await delay();
    
    for (const propertyId of Object.keys(mockEventsData)) {
      const events = mockEventsData[propertyId];
      const index = events.findIndex(e => e.id === eventId);
      
      if (index !== -1) {
        events[index] = { ...events[index], ...updates };
        return {
          success: true,
          data: events[index],
        };
      }
    }
    
    return { success: false, data: null };
  }
  
  // Use same format as create, just add id field
  const apiPayload = transformEventForAPI(updates as Omit<Event, 'id'>);
  const apiUpdates = {
    ...apiPayload,
    id: eventId, // Add id field for update
  };
  
  const response = await apiClient.post<ApiResponse<unknown>>(
    API_ENDPOINTS.events.create,
    apiUpdates
  );
  
  const transformedEvent = response.data.data 
    ? transformEventFromAPI(response.data.data as RawApiEvent) 
    : null;
  
  return {
    success: response.data.success,
    data: transformedEvent,
  };
}

export async function deleteEvent(eventId: string): Promise<ApiResponse<null>> {
  if (USE_MOCK) {
    await delay();
    
    for (const propertyId of Object.keys(mockEventsData)) {
      const events = mockEventsData[propertyId];
      const index = events.findIndex(e => e.id === eventId);
      
      if (index !== -1) {
        events.splice(index, 1);
        return { success: true, data: null };
      }
    }
    
    return { success: false, data: null };
  }
  
  // New API payload format
  const payload = [
    {
      resource_name: "RevenueEvent",
      column_value_pair: {
        ids: [Number(eventId)]
      }
    }
  ];
  
  const response = await apiClient.delete<ApiResponse<null>>(
    API_ENDPOINTS.generic,
    { data: payload }
  );
  return response.data;
}

export async function exportEvents(propertyId: string): Promise<ApiResponse<Event[]>> {
  if (USE_MOCK) {
    await delay();
    // Get all events that include this property
    const allEvents: Event[] = [];
    Object.values(mockEventsData).forEach(events => {
      events.forEach(event => {
        if (event.propertyIds.includes(propertyId) && !allEvents.find(e => e.id === event.id)) {
          allEvents.push(event);
        }
      });
    });
    return {
      success: true,
      data: allEvents,
    };
  }
  
  // Use generic API endpoint with filters parameter
  const filters = JSON.stringify([{
    resource_name: "RevenueEvent",
    columns: ["*"],
    column_filters: [{
      org_id: propertyId
    }]
  }]);
  
  const response = await apiClient.get<any>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`
  );
  
  // Extract data from nested structure
  const rawEvents = (response.data?.data?.[0] as any)?.RevenueEvent_data || [];
  const transformedEvents = rawEvents.map((rawEvent: unknown) => 
    transformEventFromAPI(rawEvent as RawApiEvent)
  );
  
  return {
    success: response.data.success,
    data: transformedEvents,
  };
}

export async function importEvents(
  targetPropertyIds: string[],
  events: Omit<Event, 'id'>[]
): Promise<ApiResponse<{ importedCount: number }>> {
  if (USE_MOCK) {
    await delay(500);
    let importedCount = 0;
    
    for (const event of events) {
      const newEvent: Event = {
        ...event,
        id: `event-${Date.now()}-${importedCount}`,
        propertyIds: targetPropertyIds,
      };
      
      // Store under first property
      const firstPropertyId = targetPropertyIds[0] || '1';
      if (!mockEventsData[firstPropertyId]) {
        mockEventsData[firstPropertyId] = [];
      }
      mockEventsData[firstPropertyId].push(newEvent);
      importedCount++;
    }
    
    return {
      success: true,
      data: { importedCount },
    };
  }
  
  // Transform events to API format
  const apiEvents = events.map(event => transformEventForAPI(event));
  
  const response = await apiClient.post<ApiResponse<{ importedCount: number }>>(
    API_ENDPOINTS.events.import,
    { targetPropertyIds, events: apiEvents }
  );
  return response.data;
}
